// Modules
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTracking } from 'react-tracking'
import { ErrorResponse } from 'apollo-link-error'

// Application
import { useApp, useSignUp } from 'context'
import { clearStorage, isEmpty } from 'utils'
import { useLoginHook } from 'hooks'
import { tracking, OutputErrorCode, OutputErrorMsg, ROUTES_PATH } from 'constants/index'
import { trackData } from './tracking'
import { StatusMessage, ContactType } from 'typings'
import { ResendTokenType } from 'typings/account'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { useSignUpStepper } from 'redesign/hooks/useSignUpStepper'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'

// Components
import { TemplateContainer } from 'components'
import { ValidateDestinationToken, ValidateTokenInitial } from 'components/Presenters/ValidateDestinationToken'
import { SignUpHeader as Header } from 'redesign/components/organisms'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { goToPage, goToPageLogin } from 'redesign/utils/goToPage'

export const SNValidateToken: React.FC = () => {
    const history = useHistory()

    const [isLoading, setLoading] = useState<boolean>(false)
    const [isDisableButton, setIsDisableButton] = useState(false)
    const [isLoadingButton, setIsLoadingButton] = useState(false)
    const [fieldState, setFieldState] = useState<StatusMessage>({ status: '', message: '' })
    const { verifyUser } = useLoginHook()
    const { trackEvent } = useTracking()
    const { getConfigByName, isFeatureEnabled } = useRemoteConfig()
    const { hasMounted, hasSeenFirstStep, resendToken, contact, confirmToken, userId, personalData, passwords } = useSignUp()
    const { openToast } = useApp()
    const { phasedLoginVisibility } = usePhasedLogin()
    const { setStepValue } = useSignUpStepper()
    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')
    const shouldRedirect = !personalData?.cpf || !personalData.biologicalSex || !passwords?.password || !hasSeenFirstStep

    const isPreRegisterUser = !isEmpty(userId)

    const { dispatchAnalyticsEvent } = useSignUpAnaytics({
        defaultDispatchEvent: 'confirmTokenOnOpen',
        shouldCall: hasMounted && !shouldRedirect,
    })

    const SUBTITLE_CONTENT: string = useMemo(() => {
        let subtitle = 'Um código de segurança foi enviado para o [CONTACT_TYPE] <span>[CONTACT_VALUE]</span>.'

        switch (contact?.contactType) {
            case ContactType.EMAIL: {
                subtitle = subtitle.replace('[CONTACT_VALUE]', contact?.email?.value || '')
                break
            }
            case ContactType.MOBILE:
            default: {
                subtitle = subtitle.replace('[CONTACT_VALUE]', contact?.cellphone?.value || '')
                break
            }
        }

        subtitle = subtitle.replace('[CONTACT_TYPE]', contact?.contactType === ContactType.EMAIL ? 'e-mail' : 'celular')

        return subtitle
    }, [contact])

    useEffect(() => {
        setStepValue(3)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (!hasMounted) {
        return null
    }

    if (shouldRedirect) {
        history.push(ROUTES_PATH.SIGN_UP_PERSONAL_DATA)
        return null
    }

    async function handleSubmit(values: ValidateTokenInitial) {
        setIsDisableButton(true)
        setFieldState({ status: 'loading', message: 'Validando código' })
        const { token: rawToken } = values
        const token = rawToken.join('')
        setIsLoadingButton(true)

        const trackinData = trackData({
            isPreRegisterUser,
            biologicalSex: personalData?.biologicalSex,
            contactType: contact?.contactType!,
        })

        sessionStorage.setItem('@LiviaAuth:PhoneToken', token)
        try {
            const successfull = await confirmToken(token)

            trackEvent(trackinData.vpageview)
            trackEvent(trackinData.stepFunnel)

            setFieldState({ status: 'success', message: 'Código validado' })
            clearStorage([
                '@LiviaAuth:SignUpPersonalData',
                '@LiviaAuth:SignUpBiologicalSex',
                '@LiviaAuth:SignUpPostalCode',
                '@LiviaAuth:SignUpPhoneNumber',
                '@LiviaAuth:SignUpUserId',
                '@LiviaAuth:BringRevenueData',
            ])
            if (successfull) {
                const { isRedirect } = await verifyUser({
                    externalStep: phasedLoginVisibility ? 'PHASED_LOGIN_CADASTRO' : 'LOGIN',
                    loginType: tracking.LOGIN_TYPE.SIGN_UP,
                })

                if (!isRedirect) {
                    setTimeout(() => {
                        if (!newRedirectFlow) {
                            const redirectTo = sessionStorage.getItem('@LiviaAuth:Campaigns')
                            return goToPage(
                                redirectTo
                                    ? redirectTo
                                    : defaultConfig.strings.AUTH_REDIRECT_LOGIN || getConfigByName('AUTH_REDIRECT_LOGIN'),
                            )
                        }
                        goToPage(ROUTES_PATH.FORWARDING_APP)
                    }, 600)
                }
                dispatchAnalyticsEvent('signUpSuccess')
                dispatchAnalyticsEvent('confirmTokenOnVerifyToken')
            } else {
                setTimeout(() => {
                    goToPageLogin()
                }, 3600)
            }
            sessionStorage.removeItem('EXTERNAL_COMPANY_DATA')
        } catch (err) {
            setIsLoadingButton(false)
            const e: ErrorResponse = err
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                const codeError = e.graphQLErrors[0].extensions?.code
                if (codeError === OutputErrorCode.INVALIDTOKEN || codeError === OutputErrorCode.EXPIREDTOKEN) {
                    setFieldState({
                        status: 'error',
                        message:
                            OutputErrorCode.INVALIDTOKEN === codeError ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })
                    dispatchAnalyticsEvent('confirmTokenOnVerifyToken', {
                        errorMessage:
                            OutputErrorCode.INVALIDTOKEN === codeError ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })

                    return
                }
            }
            setFieldState({ status: '', message: '' })
            openToast('Ocorreu um erro')
            dispatchAnalyticsEvent('confirmTokenOnVerifyToken', { errorMessage: 'Ocorreu um erro' })
        }
    }

    const handleResendTokenModal = async (contactType: ContactType) => {
        try {
            setLoading(true)
            const patientLogin: ResendTokenType = {
                cellphone: contact?.cellphone?.value,
                cpf: personalData?.cpf,
                password: passwords?.password,
                destination: contactType,
            }
            await resendToken(patientLogin)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            openToast('Ocorreu um erro')
        }
    }

    return (
        <TemplateContainer>
            <Header showStepper subtitle={SUBTITLE_CONTENT} title="Insira o código" />
            <ValidateDestinationToken
                fieldState={fieldState}
                handleResendToken={handleResendTokenModal}
                handleSubmit={handleSubmit}
                isDestinationMobile={!contact?.email}
                isDestinationTaggin={false}
                isDisableButton={isDisableButton}
                isLoading={isLoading}
                isLoadingButton={isLoadingButton}
            />
        </TemplateContainer>
    )
}
